import { axios } from 'common/axios';

import { FileItem } from 'common/types/file-item.type';
import { FileCategoryExtended } from 'common/types/files';
import { FilesFolder } from 'common/types/files-folder.type';
import { FilesCreator } from 'common/types/files-creator.type';
import { FileDownloads } from 'common/types/file-downloads.type';
import { BackendSearch } from 'common/types/backend-search.type';
import { BackendFilters } from 'common/types/backend-filters.type';
import { FilesPiiOptions } from 'common/types/files-pii-options.type';
import { FileSharingData } from 'common/types/file-sharing-data.type';
import { FilesEndpointTypes } from 'common/enum/files-endpoint-types.enum';
import { FileDownloadsDates } from 'common/types/file-downloads-dates.type';

export const fetchFiles = async ({
  page,
  search,
  filter,
  pageSize,
  userEmail,
  applicationsIds,
}: {
  page: number;
  pageSize: number;
  userEmail?: string;
  search: BackendSearch;
  filter: BackendFilters;
  applicationsIds?: string[];
}): Promise<{
  data: FileItem[];
}> => {
  const { data } = await axios.post('/api/files', {
    search,
    filter,
    limit_size: pageSize,
    user_email: userEmail,
    offset: page * pageSize,
    applications_ids: applicationsIds,
  });

  return data;
};

export const fetchFilesTotal = async ({
  search,
  filter,
  userEmail,
  applicationsIds,
}: {
  userEmail?: string;
  search: BackendSearch;
  filter: BackendFilters;
  applicationsIds?: string[];
}): Promise<{ total: number }> => {
  const { data } = await axios.post('/api/files/total', {
    search,
    filter,
    user_email: userEmail,
    applications_ids: applicationsIds,
  });

  return data;
};

export const fetchFile = async (
  applicationDataIdentifier: string,
): Promise<FileItem> => {
  const { data } = await axios.post<FileItem>('/api/file', {
    application_data_identifier: applicationDataIdentifier,
  });

  return data;
};

export const fetchFilesCategories = async (): Promise<{
  data: FileCategoryExtended[];
  metadata: {
    total: number;
  };
}> => {
  const { data } = await axios.get('/api/files/categories');

  return data;
};

export const fetchFilesCreators = async (): Promise<{
  data: FilesCreator[];
}> => {
  const { data } = await axios.post('/api/files/creators');

  return data;
};

export const fetchFilesFolders = async ({
  folders,
}: {
  folders: string[];
}): Promise<{
  data: FilesFolder[];
  metadata: {
    total: number;
  };
}> => {
  const { data } = await axios.post('/api/files/folders', {
    folders,
  });

  return data;
};

export const fetchFilesOptions = async ({
  field,
  value = '',
}: {
  field: string;
  value?: string;
}): Promise<{ data: FileItem[] }> => {
  const { data } = await axios.post('/api/files/options', { field, value });

  return data;
};

export const fetchFilesPiiOptions = async (): Promise<{
  data: FilesPiiOptions;
}> => {
  const { data } = await axios.get('/api/files/pii-options');

  return data;
};

export const fetchFileSharingData = async ({
  dataIdentifier,
}: {
  dataIdentifier: string;
}): Promise<{
  total: number;
  content: FileSharingData[];
}> => {
  const url = '/api/applications/data/sharing';
  const { data } = await axios.post(url, {
    data_identifier: dataIdentifier,
  });

  return data;
};

export const fetchFileDownloadsData = async (
  fileId: string,
): Promise<{
  content: FileDownloads[];
}> => {
  const url = '/api/files/downloads';
  const { data } = await axios.post(url, {
    file_identifier: fileId,
  });

  return data;
};

export const fetchFileDownloadsDatesData = async (
  fileId: string,
  userEmail: string,
): Promise<{
  content: FileDownloadsDates[];
}> => {
  const url = '/api/applications/files_downloads/file/user';
  const { data } = await axios.post(url, {
    user_email: userEmail,
    file_identifier: fileId,
  });

  return data;
};

export const exportFiles = async ({
  ids,
  backendSearch,
  backendFilters,
}: {
  ids: string[];
  backendSearch: Record<string, any>;
  backendFilters: Record<string, any>;
}): Promise<any> => {
  const response = await axios.post(
    '/api/files/export',
    {
      list_ids: ids,
      search: backendSearch,
      filters: backendFilters,
      type: FilesEndpointTypes.ENDPOINT,
    },
    {
      responseType: 'blob',
    },
  );

  return response;
};

export const deleteFiles = async ({
  ids,
  type,
  backendSearch,
  backendFilters,
}: {
  ids: string[];
  type: FilesEndpointTypes;
  backendSearch: Record<string, any>;
  backendFilters: Record<string, any>;
}): Promise<any> => {
  const { data } = await axios.post('/api/files/delete', {
    list_ids: ids,
    device_type: type,
    search: backendSearch,
    filters: backendFilters,
  });

  return data;
};

export const quarantineFiles = async ({
  ids,
  type,
  backendSearch,
  backendFilters,
}: {
  ids: string[];
  type: FilesEndpointTypes;
  backendSearch: Record<string, any>;
  backendFilters: Record<string, any>;
}): Promise<any> => {
  const url = '/api/files/quarantine';
  const { data } = await axios.post(url, {
    list_ids: ids,
    device_type: type,
    search: backendSearch,
    filters: backendFilters,
  });

  return data;
};

export const setFilesFalseClassification = async ({
  files,
  reason,
  categoryId,
  categoryName,
  isRemoveCategories,
}: {
  reason: string;
  files: FileItem[];
  categoryId: string;
  categoryName: string;
  isRemoveCategories: boolean;
}): Promise<void> => {
  const url = '/api/files/false-classification';

  const { data } = await axios.post(url, {
    files,
    reason,
    category_id: categoryId,
    category_name: categoryName,
    is_remove_categories: isRemoveCategories,
  });

  return data;
};

export const setFilesLabel = async ({
  ids,
  type,
  isRemove,
  categoryId,
  isSetLabel,
  backendSearch,
  backendFilters,
}: {
  ids: string[];
  isRemove: boolean;
  categoryId: string;
  isSetLabel: boolean;
  type: FilesEndpointTypes;
  backendSearch: Record<string, any>;
  backendFilters: Record<string, any>;
}): Promise<any> => {
  const url = '/api/files/label';
  const { data } = await axios.post(url, {
    list_ids: ids,
    device_type: type,
    is_remove: isRemove,
    set_label: isSetLabel,
    search: backendSearch,
    filters: backendFilters,
    category_id: categoryId,
  });

  return data;
};

export const revokeFilesPermissions = async ({
  ids,
  revokeRules,
  backendSearch,
  backendFilters,
}: {
  ids: string[];
  revokeRules: any;
  backendSearch: Record<string, any>;
  backendFilters: Record<string, any>;
}): Promise<any> => {
  const url = '/api/files/revoke-permissions';
  const { data } = await axios.post(url, {
    file_ids: ids,
    search: backendSearch,
    filters: backendFilters,
    revoke_rules: revokeRules,
  });

  return data;
};

export const downloadFile = async (fileId: string): Promise<Blob> => {
  const { data } = await axios.post(
    '/api/download-file/',
    {
      convert: true,
      file_id: fileId,
    },
    {
      timeout: 30000,
      responseType: 'blob',
    },
  );

  return data;
};
